import React, { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import BrandSeal from './BrandSeal';

const WhatsAppIcon = ({ className = 'h-7 w-7' }) => (
  <svg
    aria-hidden="true"
    className={className}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M27.281 4.688C24.313 1.719 20.344 0.062 16.125 0.062C7.438 0.062 0.406 7.094 0.406 15.781C0.406 18.562 1.125 21.281 2.5 23.688L0.312 31.688L8.5 29.531C10.813 30.781 13.406 31.438 16.094 31.438H16.125C24.781 31.438 31.844 24.406 31.844 15.719C31.844 11.5 30.188 7.656 27.281 4.688Z"
      fill="currentColor"
    />
    <path
      d="M16.125 5.031C10.156 5.031 5.344 9.844 5.344 15.75C5.344 18.031 6.063 20.25 7.406 22.094L7.688 22.469L6.906 25.313L9.813 24.563L10.188 24.781C11.938 25.844 14 26.406 16.094 26.406H16.125C22.031 26.406 26.844 21.594 26.875 15.719C26.875 12.875 25.781 10.188 23.781 8.188C21.781 6.156 19.094 5.031 16.125 5.031Z"
      fill="#25D366"
    />
    <path
      d="M22.594 18.781C22.25 18.594 20.531 17.75 20.219 17.625C19.906 17.5 19.656 17.438 19.438 17.75C19.219 18.063 18.594 18.781 18.406 19C18.219 19.219 18.031 19.25 17.688 19.063C15.688 18.063 14.406 17.281 13.094 15.031C12.75 14.469 13.438 14.5 14.094 13.188C14.219 12.969 14.156 12.781 14.063 12.594C13.969 12.406 13.25 10.688 12.969 10.031C12.688 9.375 12.406 9.469 12.188 9.469C11.969 9.469 11.719 9.469 11.469 9.469C11.219 9.469 10.813 9.563 10.469 9.938C10.125 10.313 9.156 11.219 9.156 13.094C9.156 14.969 10.5 16.781 10.688 17.031C10.875 17.281 13.469 21.281 17.406 22.813C19.875 23.781 20.844 23.875 22.063 23.688C22.813 23.563 24.344 22.875 24.656 22C24.969 21.125 24.969 20.406 24.875 20.219C24.781 20.031 24.531 19.938 24.188 19.781C23.844 19.594 22.906 19.125 22.594 18.781Z"
      fill="white"
    />
  </svg>
);

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const handleAboutNavigation = useCallback((event) => {
    if (location.pathname === '/about') {
      event.preventDefault();
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }

    navigate('/about');
  }, [location.pathname, navigate]);

  return (
    <>
      <a
        href="https://wa.me/917760078388"
        target="_blank"
        rel="noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-4 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-[#25D366] text-white shadow-[0_18px_45px_rgba(37,211,102,0.35)] transition-transform duration-200 hover:scale-105 hover:bg-[#20bd5c] focus:outline-none focus:ring-2 focus:ring-white/80 focus:ring-offset-2 focus:ring-offset-[#140c09] sm:bottom-8 sm:right-6"
        data-testid="floating-whatsapp-button"
      >
        <WhatsAppIcon className="h-7 w-7" />
      </a>

      <footer
        className="border-t border-[#c9a45b] bg-[linear-gradient(180deg,#241711_0%,#140c09_100%)] text-white"
        data-testid="footer"
      >
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="flex flex-col items-center text-center md:items-start md:text-left">
              <BrandSeal className="h-36 w-36 sm:h-40 sm:w-40 md:self-center" />
              <p className="mt-5 max-w-sm leading-relaxed text-white/92">
                South Indian jewelry with heirloom warmth, crafted to feel storied, ceremonial, and quietly luxurious.
              </p>
            </div>

            <div>
              <h4 className="mb-4 text-lg font-heading font-medium text-[#fff8eb]">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-white/92 transition-colors hover:text-[#f8d289]" data-testid="footer-home-link">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/products" className="text-white/92 transition-colors hover:text-[#f8d289]" data-testid="footer-products-link">
                    Collections
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-white/92 transition-colors hover:text-[#f8d289]"
                    data-testid="footer-about-link"
                    onClick={handleAboutNavigation}
                  >
                    About Us
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="mb-4 text-lg font-heading font-medium text-[#fff8eb]">Contact Us</h4>
              <div className="space-y-3">
                <p className="text-white/92">Panchami Mallya Bellare</p>
                <a href="tel:+917760078388" className="flex items-center space-x-2 text-white/92 transition-colors hover:text-[#f8d289]" data-testid="footer-phone">
                  <Phone className="h-4 w-4" />
                  <span>+91 77600 78388</span>
                </a>
                <a href="mailto:panchamimallyabellare@gmail.com" className="flex items-center space-x-2 text-white/92 transition-colors hover:text-[#f8d289]" data-testid="footer-email">
                  <Mail className="h-4 w-4" />
                  <span>panchamimallyabellare@gmail.com</span>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 border-t border-[#5f4430] pt-8 text-center text-white/75">
            <p>&copy; {new Date().getFullYear()} Panchami. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
